import { useState } from 'react';
import axios from 'axios';
import PublicHeader from '../components/PublicHeader';
import { 
  Handshake,
  Building2,
  BookOpen,
  CheckCircle,
  ArrowLeft
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PartnerPortal() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    partner_name: '',
    partner_email: '',
    partner_phone: '',
    organization: '',
    training_title: '',
    training_description: '',
    training_category: '',
    duration_hours: '',
    proposed_price: '',
    max_participants: '',
    location: '',
    additional_notes: ''
  });

  const resetForm = () => {
    setFormData({
      partner_name: '',
      partner_email: '',
      partner_phone: '',
      organization: '',
      training_title: '',
      training_description: '',
      training_category: '',
      duration_hours: '',
      proposed_price: '',
      max_participants: '',
      location: '',
      additional_notes: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        duration_hours: parseInt(formData.duration_hours),
        proposed_price: parseFloat(formData.proposed_price),
        max_participants: parseInt(formData.max_participants),
      };

      await axios.post(`${API}/partner/proposals`, submitData);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-12 text-center">
          <div className="mx-auto mb-6 w-24 h-24 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="text-green-500" size={64} />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-4" data-testid="partner-success">
            Merci pour votre proposition ! 🎉
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            Votre proposition de formation a bien été reçue. Nous l'étudions attentivement et reviendrons vers vous rapidement par email.
          </p>
          <div className="flex gap-3 justify-center">
            <a href="/catalogue" className="btn-violet">Voir le catalogue</a>
            <button
              onClick={() => {
                setSubmitted(false);
                resetForm();
              }}
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Nouvelle proposition
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50">
      <PublicHeader />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-violet-100 rounded-full">
              <Handshake className="text-violet-600" size={48} />
            </div>
          </div>
          <h2 className="text-4xl font-bold text-gray-800 mb-4" data-testid="partner-title">
            Devenez partenaire
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Proposez vos formations à notre catalogue Insitumed. Ensemble, enrichissons notre offre pédagogique.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-violet-100">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Building2 className="text-violet-500" size={24} />
                Vos informations
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom complet *</label>
                  <input
                    type="text"
                    value={formData.partner_name}
                    onChange={(e) => setFormData({...formData, partner_name: e.target.value})}
                    className="input-violet"
                    required
                    data-testid="partner-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Organisation</label>
                  <input
                    type="text"
                    value={formData.organization}
                    onChange={(e) => setFormData({...formData, organization: e.target.value})}
                    className="input-violet"
                    data-testid="partner-org-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.partner_email}
                    onChange={(e) => setFormData({...formData, partner_email: e.target.value})}
                    className="input-violet"
                    required
                    data-testid="partner-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                  <input
                    type="tel"
                    value={formData.partner_phone}
                    onChange={(e) => setFormData({...formData, partner_phone: e.target.value})}
                    className="input-violet"
                    data-testid="partner-phone-input"
                  />
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-100">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <BookOpen className="text-violet-500" size={24} />
                Votre proposition de formation
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Titre de la formation *</label>
                  <input
                    type="text"
                    value={formData.training_title}
                    onChange={(e) => setFormData({...formData, training_title: e.target.value})}
                    className="input-violet"
                    required
                    data-testid="partner-training-title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                  <textarea
                    value={formData.training_description}
                    onChange={(e) => setFormData({...formData, training_description: e.target.value})}
                    className="input-violet"
                    rows="4"
                    required
                    data-testid="partner-training-description"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie *</label>
                    <input
                      type="text"
                      value={formData.training_category}
                      onChange={(e) => setFormData({...formData, training_category: e.target.value})}
                      className="input-violet"
                      placeholder="Ex: Santé, Management..."
                      required
                      data-testid="partner-training-category"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Durée (heures) *</label>
                    <input
                      type="number"
                      value={formData.duration_hours}
                      onChange={(e) => setFormData({...formData, duration_hours: e.target.value})}
                      className="input-violet"
                      min="1"
                      required
                      data-testid="partner-duration"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Prix proposé (€) *</label>
                    <input
                      type="number"
                      value={formData.proposed_price}
                      onChange={(e) => setFormData({...formData, proposed_price: e.target.value})}
                      className="input-violet"
                      min="0"
                      step="0.01"
                      required
                      data-testid="partner-price"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nb max participants *</label>
                    <input
                      type="number"
                      value={formData.max_participants}
                      onChange={(e) => setFormData({...formData, max_participants: e.target.value})}
                      className="input-violet"
                      min="1"
                      required
                      data-testid="partner-max-participants"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lieu proposé *</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="input-violet"
                    required
                    data-testid="partner-location"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes additionnelles</label>
                  <textarea
                    value={formData.additional_notes}
                    onChange={(e) => setFormData({...formData, additional_notes: e.target.value})}
                    className="input-violet"
                    rows="3"
                    placeholder="Toute information complémentaire..."
                    data-testid="partner-notes"
                  />
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-100">
              <button
                type="submit"
                disabled={loading}
                className="btn-violet w-full py-4 text-lg"
                data-testid="partner-submit"
              >
                {loading ? 'Envoi en cours...' : 'Envoyer ma proposition'}
              </button>
              <p className="text-xs text-gray-500 text-center mt-4">
                Nous étudierons votre proposition et reviendrons vers vous par email dans les meilleurs délais.
              </p>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
